'use client';

import Image from "next/image";
import clsx from 'clsx';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { isLoginAtom, showHomePlayerAtom, showUserCenterDrawerAtom, userAtom } from '@/store';
import { showLoginModal } from '@/features/auth/components/login-form';
import { Env } from '@/libs/env';
import useFn from '@/hooks/use-fn';
import BalanceBar from '@/app/[locale]/(main)/(non-activity)/components/balance-bar';
import { useRouter, usePathname, Link } from '@/i18n/navigation';
import { track } from "@/utils/track";
import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
// import NotifyBanner from '@/features/notifications/components/notify-banner';
import RewardEntry from "@/app/[locale]/(main)/(non-activity)/components/reward-entry";
import styles from './index.module.css';
import SelectLang from "@/app/[locale]/(main)/(non-activity)/components/select-lang";
import UserCenterEntry from "@/app/[locale]/(main)/(non-activity)/components/user-canter-entry";
import { getLocalLanguage } from "@/utils/storage";
import useSwitchLanguage from "@/features/user-center/hooks/useSwitchLanguage";
import { closeUserCenter } from "@/features/user-center";
import { AudioId, useAudioHelper } from "@/utils/audio-hepler";
import HomeAutoPlayer from "@/features/activity/homeAutoPlayer";
import { ActivitySourceEnum, LOGIN_SOURCE_MAP } from "@/const/report";
import { useNetwork, useThrottleFn } from "ahooks";
import toast from "react-hot-toast";
import shadowImg from '@/features/home/images/home-play-shadow.png';
import { getParents } from "@/utils/node";
import { useActivityList } from "@/features/activity/store/list";
import { useNormalizedLocale } from '@/hooks/use-normalized-locale';
import { useShowAppBanner } from '../tab-menu/hooks/use-show-appbanner';
import { useAppSafearea } from "@/hooks/use-app-safearea";

// 懒加载 GullakCashEntry
const GullakCashEntry = lazy(() => import("@/app/[locale]/(main)/(non-activity)/components/gullak-cash-entry"));

const AppBannerInfo = lazy(() => import('@/features/download/components/banner'));
/**
 * 匹配动态路由
 * @param pathname 当前路径
 * @param routePattern 路由模式，支持 :param 格式，如 '/casino/games/container/:id'
 * @returns 是否匹配
 */
// function matchRoute(pathname: string, routePattern: string): boolean {
//   // 将路由模式中的 :param 转换为正则表达式的 [^/]+（匹配除 / 外的任意字符）
//   const regexPattern = routePattern.replace(/:[^/]+/g, '[^/]+');
//   const regex = new RegExp(`^${regexPattern}$`);
//   return regex.test(pathname);
// }

/**
 * 检查路径是否匹配路由列表（支持静态路由和动态路由）
 * @param pathname 当前路径
 * @param routes 路由列表，支持静态路由字符串和动态路由模式（:param 格式）
 * @returns 是否匹配
 */
// function matchRoutes(pathname: string, routes: string[]): boolean {
//   return routes.some(route => {
//     // 如果包含 :param，使用动态路由匹配
//     if (route.includes(':')) {
//       return matchRoute(pathname, route);
//     }
//     // 否则精确匹配
//     return pathname === route;
//   });
// }

interface Props {
  onLoginSuccess?: () => void;
}

function Header({ onLoginSuccess }: Props) {
  const t = useTranslations('auth');
  const tHome = useTranslations('home');
  const [user] = useAtom(userAtom);
  const isLogin = useAtomValue(isLoginAtom);
  const router = useRouter();
  const pathname = usePathname();
  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });
  const userCenterRef = useRef(null);
  const { topSafe } = useAppSafearea();
  const [, setResize] = useState({});

  const handleLogoClick = useCallback((e) => {
    e.preventDefault();
    playAudio();
    if (pathname === '/') {
      // 清理 URL 参数后再 reload
      window.history.replaceState({}, '', window.location.pathname);
      window.location.reload();
    } else {
      router.push('/');
    }
  }, [pathname, router]);

  const handleShowLogin = useFn(() => {
    playAudio();
    showLoginModal({
      source: LOGIN_SOURCE_MAP.Top,
    }).then(onLoginSuccess);
    track('register_login_entrance_click', {
      current_module: LOGIN_SOURCE_MAP.Top,
    });
  });

  const locale = useNormalizedLocale();
  const { handleLanguageChange } = useSwitchLanguage();

  useEffect(() => {
    const localLanguage = getLocalLanguage();
    if (localLanguage !== locale && localLanguage !== '') {
      handleLanguageChange(localLanguage);
    }
  }, [locale]);

  const closeUserCenterDrawer = (e: React.MouseEvent | React.TouchEvent) => {
    if (userCenterRef.current) {
      const parents = getParents(e.target);
      if (
        parents?.filter(node =>
          [userCenterRef.current].includes(node as any)
        ).length === 0
      ) {
        closeUserCenter();
      }
    }
  };

  const { run: showError } = useThrottleFn(() => {
    toast(tHome('networkError'));
  }, {
    wait: 5 * 1000,
    trailing: false,
  });
  // 监听网络状态
  const networkState = useNetwork();
  useEffect(() => {
    if (!networkState?.online) {
      showError();
    }
  }, [networkState]);
  const { activityList } = useActivityList(ActivitySourceEnum.homeBanner);

  const showHomePlayer = useAtomValue(showHomePlayerAtom);
  const showUserCenterDrawer = useAtomValue(showUserCenterDrawerAtom);
  const isShowAppBanner = useShowAppBanner();
  const bannerHeight = isShowAppBanner ? 65 : 0;
  const headerHeight = 61 + topSafe;
  const headerTop = topSafe + bannerHeight;

  useEffect(() => {
    const handleResize = () => {
      setResize({});
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {isShowAppBanner && <Suspense fallback={null}>
        <AppBannerInfo />
      </Suspense>}
      <div className='fixed top-0 left-0 right-0 z-[49]'
        style={{
          background: 'linear-gradient(180deg, #101219, #202532)',
          paddingTop: headerTop,
        }}
        onClick={closeUserCenterDrawer}
      >
        <header
          className={clsx('flex flex-none h-[61px] sticky top-0 z-[49]', styles.header)}
        >
          {/* PC Header */}
          <div className="flex flex-1 items-center justify-between px-[12px] relative">
            {/* Center - Logo */}
            <div className="flex-1 flex select-none">
              <Link href="/" onClick={handleLogoClick}>
                <Image className="active:scale-90 select-none" src={`${Env.NEXT_PUBLIC_BRAND_ASSETS_BASE}/logo.png?v=${Env.NEXT_PUBLIC_BRAND_ASSETS_VERSION}`} alt="logo" width={0} height={0} style={{ maxHeight: '29px', width: 'auto', aspectRatio: '103/57' }} priority />
              </Link>
            </div>

            {/* Right Section */}
            <div className="flex items-end">
              {/* User info */}
              <div className='relative flex space-x-2'>
                {!isLogin
                  ?
                  <Suspense fallback={null}>
                    <Button
                      variant="primary"
                      size="sm"
                      style={{ height: '30px', paddingLeft: 10, paddingRight: 10 }}
                      className="mt-[3px]"
                      onClick={handleShowLogin}>
                      {t('loginSignup')}
                    </Button>
                  </Suspense>
                  :
                  <Suspense fallback={null}><BalanceBar /></Suspense>
                }
                <Suspense fallback={null}>
                  <GullakCashEntry />
                </Suspense>
                <Suspense fallback={null}>
                  <RewardEntry />
                  {!isLogin && <SelectLang />}
                </Suspense>
                {
                  user.accessToken && <div ref={userCenterRef} className="!ml-[7px] "><UserCenterEntry /></div>
                }
              </div>
            </div>
            {/* <NotifyBanner /> */}
          </div>
        </header>
        {
          activityList.length > 0 && showHomePlayer && !showUserCenterDrawer && (
            <div
              className="absolute right-0 bottom-0 translate-y-[100%] z-[49] overflow-hidden h-[59px] duration-300 ease-in-out w-[80px]"
            >
              <div
                className="transition-transform duration-300 ease-in-out"
                style={{
                  transform: 'translateX(50px)'
                }}
              >
                <Image src={shadowImg} alt="shadow" className="absolute z-[-1] h-[55px] w-[44.68px] left-[-4px] top-0" height={56} width={45} />
                <HomeAutoPlayer />
              </div>
            </div>
          )
        }
      </div >
      {/* Header 占位符，避免内容被固定 Header 遮挡 */}
      <div className="flex-none" style={{ height: `${headerHeight + bannerHeight}px` }} />
    </>
  );
};

const WapperHeader = (props: Props) => {
  return (
    <Suspense>
      <Header {...props} />
    </Suspense>
  );
};

export default WapperHeader;;;